/**
 * The one-off Bolt Star onboarding dialog, shown once per browser to merchants in the programme.
 */
import React from 'react';
import { useIntl } from 'react-intl';

import { Button, Dialog, IconButton } from '@bolteu/clay-react';
import { Cross } from '@bolteu/kalep-react-icons';

import { $tk } from 'utils/appTranslationKey';

import illustrationSrc from './assets/bolt-star-onboarding-illustration.jpg';

const dialogTk = $tk.widgets.boltStarOnboardingDialog;

type DialogChangeEventDetails = Parameters<
  NonNullable<React.ComponentProps<typeof Dialog.Root>['onOpenChange']>
>[1];

export interface BoltStarOnboardingDialogProps {
  isOpen: boolean;
  /** Fired only by the close button: the backdrop and Escape deliberately do not dismiss. */
  onClose: () => void;
  onDismiss: () => void;
  onAction: () => void;
}

export function BoltStarOnboardingDialog({
  isOpen,
  onClose,
  onDismiss,
  onAction,
}: BoltStarOnboardingDialogProps) {
  const intl = useIntl();

  const handleOpenChange = (open: boolean, eventDetails: DialogChangeEventDetails) => {
    // block closing via shroud or ESC
    if (!open && eventDetails.reason === 'close-press') {
      onClose();
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange} disablePointerDismissal>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup className="w-full max-w-110 shadow-lg">
          <Dialog.Close
            render={
              <IconButton
                aria-label={intl.formatMessage({ id: dialogTk.closeLabel })}
                icon={<Cross />}
                size="sm"
                variant="inverted"
              />
            }
            className="absolute right-3 top-2 z-10"
          />
          {/* The whole body scrolls on short viewports instead of squeezing the text, while the
              buttons stay pinned. Only the text is the Description, so screen readers don't
              announce the title twice. */}
          <div className="bolt-font-body-m-regular grow overflow-y-auto overscroll-none text-primary">
            <img
              src={illustrationSrc}
              alt={intl.formatMessage({ id: dialogTk.illustrationAlt })}
              width={440}
              height={250}
              className="block h-auto w-full"
            />
            <Dialog.Title
              className="bolt-font-heading-m-accent m-0 px-6 pb-3 pt-4"
              // eslint-disable-next-line jsx-a11y/heading-has-content -- content added by title component
              render={<h2 />}
            >
              {intl.formatMessage({ id: dialogTk.title })}
            </Dialog.Title>
            <Dialog.Description className="m-0 overflow-visible" render={<p />}>
              {intl.formatMessage({ id: dialogTk.description })}
            </Dialog.Description>
          </div>
          <Dialog.Footer className="shrink-0 flex-col gap-2">
            <Button className="w-full" size="md" variant="primary" onClick={onAction}>
              {intl.formatMessage({ id: dialogTk.actionLabel })}
            </Button>
            <Button className="w-full" size="md" variant="tertiary" onClick={onDismiss}>
              {intl.formatMessage({ id: dialogTk.dismissLabel })}
            </Button>
          </Dialog.Footer>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
